import axios from 'axios'
import router from '@/router'
import store from '@/store'
import * as types from '@/modules/account/store/types'
import { ADMIN_CREDENTIALS_STORAGE_KEY } from '@/constants/auth'
import { getCredentials } from '@/helpers/credentials'
import {
  COMPETENCE_CLOSED_CODE,
  REPORT_LOCK_STATUS,
  REPORT_PENDING_CODE,
  REPORT_PENDING_ERROR_NAME,
} from '@/helpers/error_messages'
import { REPORT_HUB_ROUTE, REPORT_ROUTE, reportHubLocation } from '@/constants/appointment_report_v2'
import { toPendingCount } from '@/modules/account/store/report_lock'

// Rotas em que o PN travado já está onde precisa: a central e o report de um compromisso pendente.
// Nelas o 423 da trava só atualiza o store — redirecionar dali criaria um loop.
const isOnReportLockRoute = pendingIds => {
  const current = router.currentRoute
  if (!current) return false
  if (current.name === REPORT_HUB_ROUTE) return true

  return current.name === REPORT_ROUTE && pendingIds.includes(Number(current.params?.id))
}

// Várias requisições da mesma tela podem voltar 423 juntas: só a primeira redireciona. A flag é
// zerada quando a navegação termina (ou falha) e também a cada navegação concluída
// (`resetReportLockRedirect`, chamado no afterEach do router).
let redirectingToHub = false

export const resetReportLockRedirect = () => {
  redirectingToHub = false
}

const redirectToReportHub = pendingIds => {
  if (redirectingToHub) return
  redirectingToHub = true
  // Navegação inicial ainda em curso (F5, nova aba, link direto): `currentRoute` ainda é o START ('/', sem nome) e
  // o guard está aguardando REFRESH_REPORT_LOCK — ele decide com a trava que este 423 acabou de gravar. Redirecionar
  // agora cancelaria a navegação protegida (o report pendente pedido, o `?voltar=` da central). `onReady` espera a
  // primeira navegação terminar (ou roda na hora, se ela já terminou) e a decisão usa a rota resolvida.
  router.onReady(() => {
    // Outra navegação em curso: o guard dela também decide pela trava gravada.
    if (router.history.pending || isOnReportLockRoute(pendingIds)) {
      resetReportLockRedirect()
      return
    }
    redirectingToHub = true
    // A tela atual vai em `?voltar=` para o "Continuar" da central voltar a ela quando o acesso for liberado.
    router.replace(reportHubLocation(router.currentRoute))
      .catch(() => {})
      .finally(resetReportLockRedirect)
  }, resetReportLockRedirect)
}

// 423 + APPOINTMENT_REPORT_PENDING: o servidor recusou a requisição porque o PN tem report pendente.
// NUNCA desloga (o PN precisa continuar logado para reportar) e NUNCA resolve com `undefined`
// (o `.then()` da tela rodaria como falso sucesso): grava a trava, leva à central uma única vez e
// rejeita com o erro original marcado — `response` continua disponível para `getApiErrorMessage`.
const handleReportPending = error => {
  const data = error.response.data || {}
  const pendingIds = (Array.isArray(data.pending_ids) ? data.pending_ids : []).map(Number)
  store.commit(types.MUTATE_REPORT_LOCK, {
    locked: true,
    pendingIds,
    count: toPendingCount(data.pending_count, pendingIds.length),
    checkedAt: Date.now(),
  })
  redirectToReportHub(pendingIds)

  return Promise.reject(Object.assign(error, { name: REPORT_PENDING_ERROR_NAME, isReportPending: true }))
}

export const initApi = () => {
  const credentials = getCredentials(ADMIN_CREDENTIALS_STORAGE_KEY) || undefined

  axios.defaults.baseURL = process.env.VUE_APP_API_URL
  axios.defaults.withCredentials = false

  axios.interceptors.response.use(
    response => response,
    error => {
      const status = error.response?.status
      const code = error.response?.data?.code
      // A trava de report responde 423 — tratada ANTES do ramo 401/403/419, que desloga.
      if (status === REPORT_LOCK_STATUS && code === REPORT_PENDING_CODE) {
        return handleReportPending(error)
      }
      // O bloqueio de competência fechada responde 423 justamente porque este
      // interceptor não rejeita 401/403/419 — ele desloga e redireciona, e o
      // `.then()` do chamador acabaria rodando com `undefined` (falso sucesso).
      // Guarda defensiva: se algum dia o bloqueio chegar como 403, ele precisa
      // voltar para o `catch` da tela em vez de derrubar a sessão.
      if (status === 403 && code === COMPETENCE_CLOSED_CODE) {
        return Promise.reject(error)
      }
      if (status === 401 || status === 403 || status === 419) {
        // Another tab may have logged in / renewed and stored a newer token in
        // localStorage. Adopt it and retry the request once before logging out.
        // eslint-disable-next-line no-underscore-dangle
        if (status === 401 && !error.config._retry) {
          const failedToken = (error.config.headers?.Authorization || '').replace('Bearer ', '')
          const currentCredentials = getCredentials(ADMIN_CREDENTIALS_STORAGE_KEY)
          const currentToken = currentCredentials?.token
          if (currentToken && currentToken !== failedToken) {
            // eslint-disable-next-line no-underscore-dangle, no-param-reassign
            error.config._retry = true
            store.commit(types.MUTATE_TOKEN, currentCredentials)
            // eslint-disable-next-line no-param-reassign
            error.config.headers.Authorization = `Bearer ${currentToken}`
            return axios.request(error.config)
          }
        }
        // Bloqueio de manuais é informativo, não sessão inválida — deslogar
        // aqui impediria o pop-up de confirmar a leitura de aparecer.
        if (code === 'FRANCHISE_MANUAL_ACCESS_BLOCKED') {
          return Promise.reject(error)
        }
        store.commit(types.MUTATE_TOKEN_LOGOUT)
        const errorCode = code || (status === 403 ? 'NO_PERMISSION' : undefined)
        router.push({ name: 'misc-not-authorized', params: { errorCode } })
        return undefined
      }
      // Demais status (incl. 405/409/422 e o 423 APPOINTMENT_REPORT_NOT_OPEN) chegam ao `catch` da tela.
      return Promise.reject(error)
    },
  )

  // Sync the auth token across tabs via the native 'storage' event: localStorage is
  // shared, so when another tab logs in, renews or logs out this fires in every other
  // tab. We update the in-memory Authorization header (or mirror the logout). The
  // in-memory comparison guards against re-entrant storage writes looping between tabs.
  const storageKey = `${process.env.VUE_APP_ENV_NAME}_${ADMIN_CREDENTIALS_STORAGE_KEY}`
  window.addEventListener('storage', event => {
    if (event.key !== storageKey) return
    if (event.newValue) {
      try {
        const newCredentials = JSON.parse(event.newValue)
        const inMemoryToken = (axios.defaults.headers.common?.Authorization || '').replace('Bearer ', '')
        if (newCredentials?.token && newCredentials.token !== inMemoryToken) {
          store.commit(types.MUTATE_TOKEN, newCredentials)
        }
      } catch {
        // ignore malformed storage values
      }
    } else {
      // Another tab logged out — mirror it here
      store.commit(types.MUTATE_TOKEN_LOGOUT)
      router.push({ name: 'misc-not-authorized' })
    }
  })

  if (credentials) {
    store.commit(types.MUTATE_TOKEN, credentials)
    store.dispatch(types.GET_USER_DATA)
  }
}
